// Generated from scripts/linked-products.ts. Rebuild with pnpm scripts:build.
var _a;
import { showProduct } from '@theme/product-page';
import { prefersReducedMotion } from '@theme/utilities';
/** The shopper switched a group's product in place. Published on `document`, because a card is replaced by the switch. */
export class LinkedProductSelectEvent extends CustomEvent {
    static eventName = 'linked-products:select';
    constructor(detail) {
        super(LinkedProductSelectEvent.eventName, { bubbles: true, detail });
    }
}
/** How long the name of the new product stays in the polite region, so a screen reader never meets a stale name. */
const ANNOUNCEMENT_DURATION = 5000;
/** The most the swap animation can take (150 ms in `styles/wontio.css`), after which its marker is taken off. */
const SWAP_MOTION_LIMIT = 400;
/** Fades the replaced content in over the stylesheet's short motion; nothing moves when the shopper reduces motion. */
function playSwap(element) {
    if (!element || prefersReducedMotion())
        return;
    element.setAttribute('data-product-swap', '');
    const end = () => element.removeAttribute('data-product-swap');
    element.addEventListener('animationend', (event) => {
        if (event.target === element)
            end();
    }, { once: true });
    window.setTimeout(end, SWAP_MOTION_LIMIT);
}
function isPlainClick(event) {
    return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}
function choiceOf(element, handle) {
    return element.querySelector(`a[data-product-handle="${CSS.escape(handle)}"]`);
}
class LinkedProducts extends HTMLElement {
    #lifetime;
    #announceTimer;
    connectedCallback() {
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        this.addEventListener('click', this.#handleClick, { signal: lifetime.signal });
    }
    disconnectedCallback() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        window.clearTimeout(this.#announceTimer);
    }
    /** Where a choice can be switched in place, or undefined where it navigates. */
    get #surface() {
        if (typeof Shopify !== 'undefined' && Shopify.designMode === true)
            return undefined;
        if (this.closest('quick-add-dialog'))
            return undefined;
        if (this.closest('product-card'))
            return 'card';
        if (this.dataset.pageProduct === 'true' && this.closest('main'))
            return 'product';
        return undefined;
    }
    #handleClick = (event) => {
        const link = event.target instanceof Element ? event.target.closest('a[data-product-url]') : null;
        if (!link || !this.contains(link) || !isPlainClick(event))
            return;
        const surface = this.#surface;
        const { productHandle: handle, productUrl } = link.dataset;
        if (!surface || !handle || !productUrl)
            return;
        // The product on show is the one the choice names: a reload would only repeat it.
        if (link.getAttribute('aria-current') === 'page') {
            event.preventDefault();
            return;
        }
        if (surface === 'card') {
            if (this.#switchCard(link, handle))
                event.preventDefault();
            return;
        }
        event.preventDefault();
        void this.#switchPage(link, handle, new URL(productUrl, window.location.href));
    };
    async #switchPage(link, handle, url) {
        const fromProductId = this.dataset.productId ?? '';
        let html;
        try {
            html = await showProduct(url, 'push');
        }
        catch {
            // Nothing changed on the page: the shopper gets the product the ordinary way.
            window.location.assign(link.href);
            return;
        }
        // A newer choice replaced this one and reports for itself.
        if (!html)
            return;
        const main = document.querySelector('main');
        // The block normally stays in place and carries the new product; when the new page has none, the choice is gone.
        const scope = this.isConnected ? this : main?.querySelector('linked-products[data-page-product="true"]');
        const chosen = scope ? choiceOf(scope, handle) : null;
        chosen?.focus({ preventScroll: true });
        playSwap(main);
        const name = html.querySelector('main h1')?.textContent?.trim() || link.textContent?.trim() || '';
        if (scope instanceof _a)
            scope.#announce(name);
        const productId = scope?.dataset.productId || html.querySelector('main [data-product-id]')?.dataset.productId;
        if (productId)
            document.dispatchEvent(new LinkedProductSelectEvent({ productId, fromProductId, productHandle: handle, surface: 'product' }));
    }
    /**
     * Replaces the card with the prepared card of the chosen product, and keeps the card it replaced for the way back.
     * @returns whether the card was switched; false leaves the link to navigate
     */
    #switchCard(link, handle) {
        const card = this.closest('product-card');
        // A group whose entries leave out the card's own product still switches: the block names that product itself.
        const current = this.querySelector('a[aria-current]')?.dataset.productHandle ?? this.dataset.productHandle;
        if (!card || !current)
            return false;
        const prepared = Array.from(card.parentElement?.querySelectorAll(':scope > template[data-linked-card]') ?? []).find((template) => template.dataset.productHandle === handle);
        const next = prepared ? document.importNode(prepared.content, true).querySelector('product-card') : null;
        if (!prepared || !next)
            return false;
        const fromProductId = card.dataset.productId ?? '';
        const kept = document.createElement('template');
        kept.setAttribute('data-linked-card', '');
        kept.dataset.productHandle = current;
        kept.content.append(card.cloneNode(true));
        card.replaceWith(next);
        prepared.remove();
        next.after(kept);
        const scope = next.querySelector('linked-products');
        (scope ? choiceOf(scope, handle) : null)?.focus({ preventScroll: true });
        playSwap(next);
        const name = next.querySelector('[ref="productTitleLink"]')?.textContent?.trim() || link.textContent?.trim() || '';
        if (scope)
            scope.#announce(name);
        const productId = next.dataset.productId;
        if (productId)
            document.dispatchEvent(new LinkedProductSelectEvent({ productId, fromProductId, productHandle: handle, surface: 'card' }));
        return true;
    }
    /** Says `name` once through the block's polite region, which empties itself afterwards. */
    #announce(name) {
        const region = this.querySelector('[data-status]');
        if (!region || !name)
            return;
        region.textContent = '';
        window.clearTimeout(this.#announceTimer);
        // A region that changes in the task after it was emptied is announced even when the name repeats.
        window.requestAnimationFrame(() => {
            region.textContent = name;
            this.#announceTimer = window.setTimeout(() => {
                region.textContent = '';
            }, ANNOUNCEMENT_DURATION);
        });
    }
}
_a = LinkedProducts;
if (!customElements.get('linked-products')) {
    customElements.define('linked-products', LinkedProducts);
}
